import { RichTextArea } from "./RichTextArea";
import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type Ref,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from "react";
import { localizeFeedbackState, type FeedbackState } from "./feedback";
import { formatMessage } from "../i18n/catalogUtils";
import { translateShared } from "../i18n/sharedCatalog";
import { useWorkspaceCopy } from "../i18n/workspaceCatalog";
import type { StatusTone } from "./status";
import { TechnicalText, type TechnicalValueKind } from "./BidiText";
import { FeedbackDialog } from "./layers";

function classNames(...values: Array<string | false | null | undefined>): string {
  return values.filter(Boolean).join(" ");
}

export function Stack({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={classNames("kavoosh-stack", className)}>{children}</div>;
}

export function Inline({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={classNames("kavoosh-inline", className)}>{children}</div>;
}

export function Surface({ children, className }: { children: ReactNode; className?: string }) {
  return <section className={classNames("kavoosh-surface", className)}>{children}</section>;
}

export function WorkspaceShell({ navigation, children }: { navigation: ReactNode; children: ReactNode }) {
  return <div className="kavoosh-ui kavoosh-workspace-shell">
    <aside className="kavoosh-workspace-nav">{navigation}</aside>
    <main className="kavoosh-workspace-main">{children}</main>
  </div>;
}

export function Page({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={classNames("kavoosh-page", className)}>{children}</div>;
}

export function PageHeader({ title, description, actions }: { title: string; description?: string; actions?: ReactNode }) {
  return <header className="kavoosh-page-header">
    <div><h1>{title}</h1>{description ? <p>{description}</p> : null}</div>
    {actions ? <div className="kavoosh-inline">{actions}</div> : null}
  </header>;
}

export type ButtonVariant = "primary" | "secondary" | "quiet" | "danger";
export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant };

export function Button({ variant = "secondary", type = "button", className, ...props }: ButtonProps) {
  return <button {...props} type={type} className={classNames("kavoosh-button", `kavoosh-button--${variant}`, className)} />;
}

type FieldChromeProps = {
  id: string;
  label: string;
  required?: boolean | undefined;
  helperText?: string | undefined;
  error?: string | undefined;
  children: ReactNode;
};

function FieldChrome({ id, label, required, helperText, error, children }: FieldChromeProps) {
  const helperId = helperText ? `${id}-helper` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  return <div className="kavoosh-field">
    <label className="kavoosh-field__label" htmlFor={id}>
      {label}{required ? <span className="kavoosh-field__required" aria-hidden="true"> *</span> : null}
    </label>
    {children}
    {helperText ? <p id={helperId} className="kavoosh-field__helper">{helperText}</p> : null}
    {error ? <p id={errorId} className="kavoosh-field__error" role="alert">{error}</p> : null}
  </div>;
}

function fieldDescription(id: string, helperText?: string, error?: string): string | undefined {
  const values = [helperText ? `${id}-helper` : null, error ? `${id}-error` : null].filter(Boolean);
  return values.length ? values.join(" ") : undefined;
}

type FieldMeta = { id?: string; label: string; helperText?: string; error?: string; required?: boolean };

export type TextFieldProps = FieldMeta & { ref?: Ref<HTMLInputElement> } & Omit<InputHTMLAttributes<HTMLInputElement>, "id" | "className" | "required" | "aria-describedby" | "aria-invalid">;
export function TextField({ id: suppliedId, label, helperText, error, required, ...props }: TextFieldProps) {
  const generatedId = useId();
  const id = suppliedId ?? generatedId;
  return <FieldChrome id={id} label={label} helperText={helperText} error={error} required={required}>
    <input {...props} id={id} className="kavoosh-control" required={required} aria-invalid={error ? "true" : undefined} aria-describedby={fieldDescription(id, helperText, error)} />
  </FieldChrome>;
}

export type SelectFieldProps = FieldMeta & Omit<SelectHTMLAttributes<HTMLSelectElement>, "id" | "className" | "required" | "aria-describedby" | "aria-invalid">;
export function SelectField({ id: suppliedId, label, helperText, error, required, children, ...props }: SelectFieldProps) {
  const generatedId = useId();
  const id = suppliedId ?? generatedId;
  return <FieldChrome id={id} label={label} helperText={helperText} error={error} required={required}>
    <select {...props} id={id} className="kavoosh-control" required={required} aria-invalid={error ? "true" : undefined} aria-describedby={fieldDescription(id, helperText, error)}>{children}</select>
  </FieldChrome>;
}

export type TextAreaFieldProps = FieldMeta & Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "id" | "className" | "required" | "aria-describedby" | "aria-invalid">;
export function TextAreaField({ id: suppliedId, label, helperText, error, required, ...props }: TextAreaFieldProps) {
  const generatedId = useId();
  const id = suppliedId ?? generatedId;
  return <FieldChrome id={id} label={label} helperText={helperText} error={error} required={required}>
    <RichTextArea {...props} id={id} className="kavoosh-control" required={required} aria-label={typeof label === "string" ? label : undefined} aria-invalid={error ? "true" : undefined} aria-describedby={fieldDescription(id, helperText, error)} />
  </FieldChrome>;
}

export function DirectoryToolbar({ children, label }: { children: ReactNode; label?: string }) {
  const { locale } = useWorkspaceCopy();
  const resolvedLabel = label ?? translateShared(locale, "common.directoryControls");
  return <div className="kavoosh-toolbar" role="search" aria-label={resolvedLabel}>{children}</div>;
}

export type SearchFieldProps = Omit<InputHTMLAttributes<HTMLInputElement>, "type" | "className"> & { label: string; icon?: ReactNode };
export function SearchField({ label, icon, ...props }: SearchFieldProps) {
  return <label className="kavoosh-search">
    <span className="kavoosh-visually-hidden">{label}</span>
    {icon ? <span className="kavoosh-search__icon" aria-hidden="true">{icon}</span> : null}
    <input {...props} type="search" className="kavoosh-control" aria-label={label} />
  </label>;
}

export function StatusBadge({ label, tone = "neutral" }: { label: string; tone?: StatusTone }) {
  return <span className={classNames("kavoosh-status", tone !== "neutral" && `kavoosh-status--${tone}`)}>{label}</span>;
}

export function DataStatePanel({ state }: { state: FeedbackState }) {
  const { locale } = useWorkspaceCopy();
  const localized = localizeFeedbackState(locale, state);
  const signature = useMemo(
    () => `${localized.kind}\u0000${localized.title}\u0000${localized.message}`,
    [localized.kind, localized.message, localized.title],
  );
  const [dismissedSignature, setDismissedSignature] = useState<string | null>(null);
  const prominent = localized.kind === "error";
  const role = localized.kind === "error" ? "alert" : localized.announced ? "status" : undefined;
  return <>
    {prominent && dismissedSignature !== signature
      ? <FeedbackDialog state={localized} onDismiss={() => setDismissedSignature(signature)} />
      : null}
    <div className={classNames("kavoosh-data-state", `kavoosh-data-state--${localized.kind}`, `kavoosh-data-state--tone-${localized.tone}`)} role={role} aria-live={localized.kind === "loading" ? "polite" : undefined} aria-busy={localized.kind === "loading" ? "true" : undefined}>
      <div className="kavoosh-data-state__body">
        <strong>{localized.title}</strong>
        <p>{localized.message}</p>
        {localized.action ? <Button variant="secondary" onClick={localized.action.onAction}>{localized.action.label}</Button> : null}
      </div>
    </div>
  </>;
}

export type DataTableSort = {
  direction: "ascending" | "descending" | "none";
  onSort: () => void;
};

export type DataTableColumn<T> = {
  key: string;
  label: string;
  render: (row: T) => ReactNode;
  className?: string;
  sort?: DataTableSort;
};

export function DataTable<T>({
  rows,
  columns,
  getRowKey,
  caption,
  emptyState,
}: {
  rows: readonly T[];
  columns: ReadonlyArray<DataTableColumn<T>>;
  getRowKey: (row: T) => string;
  caption: string;
  emptyState?: FeedbackState;
}) {
  if (rows.length === 0 && emptyState) return <DataStatePanel state={emptyState} />;
  return <div className="kavoosh-table-wrap">
    <table className="kavoosh-table kavoosh-table--responsive">
      <caption className="kavoosh-visually-hidden">{caption}</caption>
      <thead><tr>{columns.map((column) => <th
        key={column.key}
        scope="col"
        className={column.className}
        aria-sort={column.sort && column.sort.direction !== "none" ? column.sort.direction : undefined}
      >{column.sort
          ? <button type="button" className="kavoosh-table__sort" onClick={column.sort.onSort}>{column.label}<span aria-hidden="true">↕</span></button>
          : column.label}</th>)}</tr></thead>
      <tbody>{rows.map((row) => <tr key={getRowKey(row)}>{columns.map((column) => <td key={column.key} data-label={column.label} className={column.className}>{column.render(row)}</td>)}</tr>)}</tbody>
    </table>
  </div>;
}

export function DirectoryList<T>({ rows, getRowKey, renderRow, label }: {
  rows: readonly T[];
  getRowKey: (row: T) => string;
  renderRow: (row: T) => ReactNode;
  label: string;
}) {
  return <ul className="kavoosh-directory-list" aria-label={label}>{rows.map((row) => <li key={getRowKey(row)}>{renderRow(row)}</li>)}</ul>;
}

export function Pagination({ page, totalPages, onPageChange, label }: {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  label?: string;
}) {
  const { locale } = useWorkspaceCopy();
  if (totalPages <= 0) return null;
  const boundedTotal = totalPages;
  const boundedPage = Math.min(Math.max(1, page), boundedTotal);
  const resolvedLabel = label ?? translateShared(locale, "common.pagination");
  return <nav className="kavoosh-pagination" aria-label={resolvedLabel}>
    <Button variant="secondary" onClick={() => onPageChange(boundedPage - 1)} disabled={boundedPage <= 1}>{translateShared(locale, "common.previous")}</Button>
    <span aria-live="polite">{formatMessage(translateShared(locale, "common.pageOf"), { page: boundedPage, total: boundedTotal })}</span>
    <Button variant="secondary" onClick={() => onPageChange(boundedPage + 1)} disabled={boundedPage >= boundedTotal}>{translateShared(locale, "common.next")}</Button>
  </nav>;
}

export function TechnicalValue({
  value,
  kind = "identifier",
  label,
  truncate = false,
  copyable = true,
}: {
  value: string;
  kind?: TechnicalValueKind;
  label?: string;
  truncate?: boolean;
  copyable?: boolean;
}) {
  const { locale } = useWorkspaceCopy();
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">("idle");
  const copyRequestRef = useRef(0);
  const latestValueRef = useRef(value);
  latestValueRef.current = value;

  useEffect(() => {
    copyRequestRef.current += 1;
    setCopyState("idle");
  }, [value]);

  const copy = async () => {
    const request = ++copyRequestRef.current;
    const copiedValue = value;
    try {
      if (!navigator.clipboard) throw new Error("clipboard unavailable");
      await navigator.clipboard.writeText(copiedValue);
      if (request === copyRequestRef.current && latestValueRef.current === copiedValue) setCopyState("copied");
    } catch {
      if (request === copyRequestRef.current && latestValueRef.current === copiedValue) setCopyState("failed");
    }
  };
  const buttonLabel = copyState === "copied"
    ? translateShared(locale, "common.copied")
    : copyState === "failed"
      ? translateShared(locale, "common.copyFailed")
      : translateShared(locale, "common.copy");
  return <span className="kavoosh-technical-value">
    <TechnicalText kind={kind} truncate={truncate} aria-label={label ? `${label}: ${value}` : value}>{value}</TechnicalText>
    {copyable ? <button type="button" className="kavoosh-copy-button" onClick={() => void copy()} aria-label={`${buttonLabel} ${label ?? kind}`} aria-live="polite">{buttonLabel}</button> : null}
  </span>;
}

export function TrustBoundaryNotice({ title, children }: { title?: string; children: ReactNode }) {
  const { locale } = useWorkspaceCopy();
  const resolvedTitle = title ?? translateShared(locale, "privacy.boundary");
  return <aside className="kavoosh-boundary" role="note">
    <span aria-hidden="true">ⓘ</span>
    <div><strong>{resolvedTitle}</strong><p>{children}</p></div>
  </aside>;
}
